import { useEffect, useState } from "react";
import { readCache, writeCacheExt, swrJson } from "@/lib/swr";
import { Link } from "react-router-dom";
import { SyncBar } from "@/components/SyncBar";
import { DateRange } from "@/components/DateRange";
import { Drawer } from "@/pages/a/monitoring/articles/detail";
import { Ch } from "@/components/Fa";
import { NetworkGraph, FocusView } from "@/components/NetworkGraph";
import { Panel } from "@/components/Panel";
import { Tile } from "@/components/Tile";
import { Gauge } from "@/components/viz/Gauge";
import { ProgressRow } from "@/components/viz/ProgressRow";
import { AreaChart } from "@/components/viz/AreaChart";
import { DayCols, type DayDatum } from "@/components/viz/DayCols";

const M = { pos: "var(--mark-pos)", neu: "var(--mark-neu)", neg: "var(--mark-neg)" };
const BLUE = "var(--mark-c1)";
const ORANGE = "var(--mark-c2)";
const TONE_ID: Record<string, string> = { positive: "Positif", neutral: "Netral", negative: "Negatif" };
const nf = (x: number) => (Math.abs(x ?? 0) >= 100000
  ? Intl.NumberFormat("id-ID", { notation: "compact", maximumFractionDigits: 1 }).format(x)
  : Intl.NumberFormat("id-ID").format(x ?? 0));
const pct = (a: number, b: number) => (b ? (a / b) * 100 : 0);
const wibDay = (d: string) => `${d.slice(8)}/${d.slice(5, 7)}`;

const LABEL_GROUPS: Array<[string, string, string]> = [
  ["topic", "Topik utama", "fa-solid fa-hashtag"],
  ["attribute", "Atribut", "fa-solid fa-tags"],
  ["emotion", "Emosi", "fa-regular fa-face-smile"],
  ["brand", "Brand & organisasi", "fa-solid fa-building"],
];

export default function SocialAnalyticsPage() {
  const [d, setD] = useState<any>(null);
  const [authors, setAuthors] = useState<any[]>([]);
  const [labels, setLabels] = useState<Record<string, any[]>>({});
  const [detailId, setDetailId] = useState<string | null>(null);
  const [net, setNet] = useState<any>(null);
  const [netFocus, setNetFocus] = useState<any>(null);
  const [mode, setMode] = useState<"mention" | "interaksi" | "tone">("mention");
  const today = new Date(Date.now() + 7 * 3600e3).toISOString().slice(0, 10);
  const defStart = new Date(Date.now() + 7 * 3600e3 - 6 * 86400e3).toISOString().slice(0, 10);
  const [start, setStart] = useState(defStart);
  const [end, setEnd] = useState(today);

  async function load(s2?: string, e2?: string) {
    const S = s2 ?? start, E = e2 ?? end;
    const get = (u: string) => fetch(u).then((r) => (r.ok ? r.json() : null)).catch(() => null);
    get(`/api/v1/social/labels?start=${S}&end=${E}`).then((j) => setLabels(j?.byType ?? {}));
    swrJson<any>(`/api/v1/social/authors?start=${S}&end=${E}`, (j) => setAuthors(j?.data ?? []));
    swrJson<any>(`/api/v1/social/network?start=${S}&end=${E}`, (j) => setNet(j?.data ?? null));
    const url = `/api/v1/analytics/social?start=${S}&end=${E}`;
    const cached = readCache(url);
    if (cached) setD(cached);
    const r = await fetch(url);
    if (r.status === 401) { location.href = "/login"; return; }
    const fresh = await r.json().catch(() => null);
    if (fresh) { setD(fresh); writeCacheExt(url, fresh); } else if (!cached) setD(null);
  }
  useEffect(() => { load(); }, []);

  const qs = `start=${start}&end=${end}`;
  const t = d?.tones ?? { positive: 0, neutral: 0, negative: 0 };
  const tot = t.positive + t.neutral + t.negative;
  const chans = (d?.byChannel ?? []) as any[];
  const chanSum = chans.reduce((a, c) => a + c.total, 0);
  const days: DayDatum[] = (() => {
    const map = new Map<string, DayDatum>();
    for (const r of d?.dailyTone ?? []) {
      const e = map.get(r.day) ?? { day: r.day, positive: 0, neutral: 0, negative: 0 };
      (e as any)[r.tone] = ((e as any)[r.tone] ?? 0) + r.c;
      map.set(r.day, e);
    }
    return [...map.values()].sort((a, b) => a.day.localeCompare(b.day));
  })();
  const daily = (d?.daily ?? []) as Array<{ day: string; c: number; inter: number }>;
  const ps = d?.postSplit ?? { parent: 0, reply: 0 };
  const psTot = ps.parent + ps.reply || 1;
  const engTotal = chans.reduce((a, c) => a + (c.likes ?? 0) + (c.comments ?? 0) + (c.shares ?? 0), 0);
  const reachTotal = chans.reduce((a, c) => a + (c.reach ?? 0), 0);

  return (
    <div className="avio-rise">
      <div style={{ display: "flex", alignItems: "flex-end", gap: "var(--space-6)", flexWrap: "wrap" }}>
        <div>
          <h1 className="avio-page-title" style={{ margin: 0 }}>Social Analytics</h1>
          <div className="avio-t-sm avio-muted">Neticle · {start} → {end} (WIB)</div>
        </div>
        <div style={{ marginLeft: "auto" }}>
          <DateRange start={start} end={end} onApply={(s2, e2) => { setStart(s2); setEnd(e2); load(s2, e2); }} />
        </div>
      </div>
      <SyncBar wing="social" />

      <div className="avio-tiles">
        {!d && [0, 1, 2, 3].map((i) => <div key={i} className="avio-sk" style={{ height: 128, borderRadius: "var(--radius-md)" }} />)}
        {d && <>
          <Tile c={1} icon="fa-solid fa-bullhorn" label="Total mention" value={tot} sub="percakapan" href={`/a/social/posts?${qs}`} />
          <Tile c={2} icon="fa-solid fa-circle-exclamation" label="Negatif" value={t.negative}
            sub={`${pct(t.negative, tot).toFixed(1)}% dari total`} href={`/a/social/posts?sentiment=negative&${qs}`} />
          <Tile c={3} icon="fa-solid fa-heart" label="Total interaksi" value={engTotal} sub="like + komentar + share" />
          <Tile c={5} icon="fa-solid fa-users" label="Perkiraan jangkauan" value={reachTotal} sub="reach kumulatif" />
        </>}
      </div>

      <div className="avio-grid">
        <div data-col="8">
          <Panel icon="fa-solid fa-chart-area" title="Pergerakan percakapan" hint="harian (WIB)"
            action={
              <span className="avio-tabs" role="tablist">
                <button role="tab" aria-selected={mode === "mention"} onClick={() => setMode("mention")}>Mention</button>
                <button role="tab" aria-selected={mode === "interaksi"} onClick={() => setMode("interaksi")}>Interaksi</button>
                <button role="tab" aria-selected={mode === "tone"} onClick={() => setMode("tone")}>Per tone</button>
              </span>
            }>
            {mode !== "tone" ? (
              <>
                <div className="avio-leg" style={{ marginBottom: "var(--space-5)" }}>
                  <span className="avio-leg__i">
                    <span className="avio-leg__sw" style={{ background: mode === "mention" ? BLUE : ORANGE }} />
                    {mode === "mention" ? <>Mention harian · total <b>{nf(tot)}</b></> : <>Interaksi harian · total <b>{nf(engTotal)}</b></>}
                  </span>
                  <span className="avio-sub">
                    Mention dan interaksi sengaja dipisah — skalanya berbeda jauh, menyatukannya di satu sumbu membuat deret kecil tak terbaca.
                  </span>
                </div>
                <AreaChart labels={daily.map((x) => wibDay(x.day))} series={mode === "mention"
                  ? [{ key: "mention", label: "Mention", color: BLUE, data: daily.map((x) => x.c) }]
                  : [{ key: "inter", label: "Interaksi", color: ORANGE, data: daily.map((x) => x.inter) }]} />
              </>
            ) : (
              <>
                <div className="avio-leg" style={{ marginBottom: "var(--space-5)" }}>
                  {[["Positif", M.pos], ["Netral", M.neu], ["Negatif", M.neg]].map(([l, c]) => (
                    <span key={l} className="avio-leg__i"><span className="avio-leg__sw" style={{ background: c }} />{l}</span>
                  ))}
                </div>
                <DayCols data={days} height={228} hrefOf={(x) => `/a/social/posts?start=${x.day}&end=${x.day}`} />
              </>
            )}
          </Panel>
        </div>
        <div data-col="4">
          <Panel icon="fa-solid fa-gauge-high" title="Komposisi tone">
            <div className="avio-gauges">
              <Gauge value={t.positive} total={tot} color={M.pos} label="Positif" />
              <Gauge value={t.neutral} total={tot} color={M.neu} label="Netral" />
              <Gauge value={t.negative} total={tot} color={M.neg} label="Negatif" />
            </div>
            <div style={{ marginTop: "var(--space-7)", paddingTop: "var(--space-5)", borderTop: "1px solid var(--color-border)", display: "grid", gap: "var(--space-4)" }}>
              <div className="avio-leg" style={{ justifyContent: "space-between" }}>
                <span className="avio-leg__i">Post induk</span>
                <Link to={`/a/social/posts?postType=parent&${qs}`} className="avio-leg__i"><b>{nf(ps.parent)}</b> · {pct(ps.parent, psTot).toFixed(0)}%</Link>
              </div>
              <div className="avio-prow__track">
                <span className="avio-prow__fill" style={{ flex: ps.parent || 1, background: BLUE }} />
                <span className="avio-prow__fill" style={{ flex: ps.reply || 1, background: ORANGE }} />
              </div>
              <div className="avio-leg" style={{ justifyContent: "space-between" }}>
                <span className="avio-leg__i">Reply / komentar</span>
                <Link to={`/a/social/posts?postType=reply&${qs}`} className="avio-leg__i"><b>{nf(ps.reply)}</b> · {pct(ps.reply, psTot).toFixed(0)}%</Link>
              </div>
            </div>
          </Panel>
        </div>

        <div data-col="6">
          <Panel icon="fa-solid fa-mobile-screen" title="Kanal" hint="volume · tone · engagement">
            {chans.length === 0
              ? <div className="avio-empty"><i className="fa-regular fa-circle-question avio-empty__icon" /><div className="avio-empty__title">Belum ada data kanal.</div></div>
              : <div className="avio-plist">
                  {chans.map((c) => (
                    <ProgressRow key={c.channel} name={<><Ch t={c.channel} /> {c.channel} <span className="avio-sub">· <i className="fa-solid fa-thumbs-up" /> {nf(c.likes)} · <i className="fa-solid fa-comment" /> {nf(c.comments)}</span></>}
                      href={`/a/social/posts?sourceType=${c.channel}&${qs}`} total={c.total} share={pct(c.total, chanSum)}
                      maxShare={pct(chans[0]?.total ?? 0, chanSum)}
                      segs={[
                        { value: c.positive, color: M.pos, title: `positif ${c.positive}` },
                        { value: c.neutral, color: M.neu, title: `netral ${c.neutral}` },
                        { value: c.negative, color: M.neg, title: `negatif ${c.negative}` },
                      ]} />
                  ))}
                </div>}
          </Panel>
        </div>
        <div data-col="6">
          <Panel icon="fa-solid fa-star" title="Influencer teratas" hint="peringkat engagement" flush
            action={<Link to="/a/social/influencers">Semua <i className="fa-solid fa-arrow-right" /></Link>}>
            <div className="avio-list">
              {authors.slice(0, 8).map((a: any, i: number) => {
                const p = a.posts || 1;
                return (
                  <Link key={a.authorId} className="avio-item" to={`/a/social/posts?author=${encodeURIComponent(a.handle)}&${qs}`}>
                    <span className="avio-item__rank">{i + 1}</span>
                    <span className="avio-ava">{(a.handle ?? "?").slice(0, 2).toUpperCase()}</span>
                    <span className="avio-item__main">
                      <span className="avio-item__title">@{a.handle}</span>
                      <span className="avio-item__meta">{nf(a.followers)} pengikut · {a.posts} post</span>
                      <span className="avio-prow__track" style={{ marginTop: 4, maxWidth: 190 }}>
                        {a.positive > 0 && <span className="avio-prow__fill" style={{ flex: a.positive, background: M.pos }} />}
                        {a.neutral > 0 && <span className="avio-prow__fill" style={{ flex: a.neutral, background: M.neu }} />}
                        {a.negative > 0 && <span className="avio-prow__fill" style={{ flex: a.negative, background: M.neg }} />}
                        {p === 0 && <span className="avio-prow__fill" style={{ flex: 1, background: "var(--mark-grid)" }} />}
                      </span>
                    </span>
                    <span className="avio-item__side"><i className="fa-solid fa-thumbs-up" /> {nf(a.likes)}<br /><span className="avio-muted">{nf(a.shares)} share</span></span>
                  </Link>
                );
              })}
              {authors.length === 0 && <div className="avio-empty"><i className="fa-regular fa-star avio-empty__icon" /><div className="avio-empty__title">Belum ada data author.</div><div className="avio-empty__hint">Jalankan sync social lebih dulu.</div></div>}
            </div>
          </Panel>
        </div>

        <div data-col="12">
          <Panel icon="fa-solid fa-circle-nodes" title="Topic network"
            hint="ukuran = volume · garis = sering muncul bersama · warna = tone dominan"
            action={netFocus ? <button className="avio-btn" data-variant="ghost" data-size="sm" onClick={() => setNetFocus(null)}><i className="fa-solid fa-arrow-left" /> Kembali</button> : undefined}>
            {!netFocus && net && <NetworkGraph nodes={net.nodes ?? []} edges={net.edges ?? []} height={420}
              onNode={(n) => fetch(`/api/v1/social/network?focus=${n.id}&${qs}`).then((r) => r.json()).then((j) => setNetFocus(j.data))} />}
            {!netFocus && !net && <div className="avio-empty"><i className="fa-solid fa-circle-nodes avio-empty__icon" /><div className="avio-empty__title">Graf belum terbentuk.</div></div>}
            {netFocus && <FocusView data={netFocus} onBack={() => setNetFocus(null)}
              postHref={`/a/social/posts?label=${netFocus.center.id}&${qs}`}
              satHref={(id) => `/a/social/posts?label=${id}&${qs}`} />}
          </Panel>
        </div>

        {LABEL_GROUPS.map(([lt, judul, icon]) => {
          const items = labels[lt] ?? labels[lt + "s"] ?? [];
          if (items.length === 0) return null;
          const sum = items.reduce((a: number, x: any) => a + x.count, 0);
          return (
            <div key={lt} data-col="6">
              <Panel icon={icon} title={judul} hint="label AI vendor">
                <div className="avio-plist">
                  {items.slice(0, 8).map((l: any) => (
                    <ProgressRow key={l.id} name={l.name} total={l.count} share={pct(l.count, sum)}
                      maxShare={pct(items[0]?.count ?? 0, sum)}
                      href={`/a/social/posts?label=${l.id}&labelName=${encodeURIComponent(l.name)}&${qs}`}
                      segs={[{ value: l.count, color: BLUE, title: `${l.name}: ${l.count}` }]} />
                  ))}
                </div>
              </Panel>
            </div>
          );
        })}

        {(d?.topEngaging ?? []).length > 0 && (
          <div data-col="6">
            <Panel icon="fa-solid fa-fire" title="Konten paling engaging" hint="engagement rate tertinggi" flush>
              <div className="avio-list">
                {(d.topEngaging ?? []).slice(0, 6).map((p2: any, i: number) => (
                  <a key={p2.id} className="avio-item" href="#" onClick={(e) => { e.preventDefault(); setDetailId(p2.id); }}>
                    <span className="avio-item__rank">{i + 1}</span>
                    <span className="avio-item__main">
                      <span className="avio-item__title">{((p2.title || p2.content || "(tanpa konten)").replace(/\s+/g, " ")).slice(0, 110)}</span>
                      <span className="avio-item__meta"><Ch t={p2.sourceType} /> {p2.sourceType} · <i className="fa-solid fa-thumbs-up" /> {nf(p2.likes ?? 0)} · <i className="fa-solid fa-comment" /> {nf(p2.comments ?? 0)}</span>
                    </span>
                    <span className="avio-chip" data-tone="soft"><span className="avio-chip__label">ER {(p2.engagementRate * 100).toFixed(2)}%</span></span>
                  </a>
                ))}
              </div>
            </Panel>
          </div>
        )}

        {(d?.topPosts ?? []).length > 0 && (
          <div data-col="6">
            <Panel icon="fa-solid fa-ranking-star" title="Post teratas" hint="total interaksi" flush>
              <div className="avio-list">
                {(d.topPosts ?? []).slice(0, 6).map((p: any, i: number) => (
                  <a key={p.id} className="avio-item" href="#" onClick={(e) => { e.preventDefault(); setDetailId(p.id); }}>
                    <span className="avio-item__rank">{i + 1}</span>
                    <span className="avio-item__main">
                      <span className="avio-item__title">{(p.title || "(tanpa judul)").slice(0, 110)}</span>
                      <span className="avio-item__meta"><Ch t={p.sourceType} /> {p.sourceType} · <i className="fa-solid fa-thumbs-up" /> {nf(p.likes ?? 0)} · <i className="fa-solid fa-retweet" /> {nf(p.shares ?? 0)}</span>
                    </span>
                    {p.sentimentLabel && <span className="avio-chip" data-sentiment={p.sentimentLabel}><span className="avio-chip__label">{TONE_ID[p.sentimentLabel] ?? p.sentimentLabel}</span></span>}
                  </a>
                ))}
              </div>
            </Panel>
          </div>
        )}

        {(d?.topThreads ?? []).length > 0 && (
          <div data-col="6">
            <Panel icon="fa-solid fa-comments" title="Thread teraktif" hint="jumlah item dalam satu percakapan" flush>
              <div className="avio-list">
                {(d.topThreads ?? []).map((t2: any) => (
                  <Link key={t2.threadId} className="avio-item" to={`/a/social/posts?thread=${encodeURIComponent(t2.threadId)}&start=2000-01-01&end=2100-01-01`}>
                    <span className="avio-item__main">
                      <span className="avio-item__title">{(t2.title || "(tanpa judul)").slice(0, 100)}</span>
                      <span className="avio-item__meta"><Ch t={t2.sourceType} /> {t2.sourceType}</span>
                    </span>
                    <span className="avio-item__side">{nf(t2.count)} item</span>
                  </Link>
                ))}
              </div>
            </Panel>
          </div>
        )}

        {(d?.fbReactions ?? []).length > 0 && (
          <div data-col="6">
            <Panel icon="fa-brands fa-facebook" title="Reaksi Facebook" hint="harian">
              <div className="avio-leg" style={{ marginBottom: "var(--space-5)" }}>
                <span className="avio-leg__i"><span className="avio-leg__sw" style={{ background: BLUE }} />Reactions</span>
                <span className="avio-leg__i"><span className="avio-leg__sw" style={{ background: ORANGE }} />Likes</span>
              </div>
              <AreaChart height={200} labels={(d.fbReactions ?? []).map((x: any) => wibDay(x.day))} series={[
                { key: "react", label: "Reactions", color: BLUE, data: (d.fbReactions ?? []).map((x: any) => x.reactions) },
                { key: "likes", label: "Likes", color: ORANGE, data: (d.fbReactions ?? []).map((x: any) => x.likes), fill: false },
              ]} />
            </Panel>
          </div>
        )}
      </div>
      {detailId && <Drawer id={detailId} onClose={() => setDetailId(null)} />}
    </div>
  );
}
